/**
 * Recommendations module - public API
 *
 * Provides a discovery-based recommendation system that:
 * - Uses SearchService in read-only mode
 * - Operates independently of live-search
 * - Caches results in memory and localStorage
 * - Provides recommendations as ProductResult[]
 */

// Export types and interfaces
export type { RecommendationSeed, RecommendationConfig, RecommendationCache } from './types';
export { DEFAULT_RECOMMENDATION_CONFIG, FALLBACK_SEED_QUERIES, STORAGE_KEYS } from './types';

// Export core components
export { RecommendationService } from './recommendationService';
export { getSeedQueries, addPersonalizedSeedQuery, clearPersonalizedSeedQueries } from './recommendationSource';
export { getCache, saveCache, clearCache, getCachedProducts } from './recommendationStorage';

// Internal registry for recommendation state
interface RecommendationRegistry {
  products: import('./types').RecommendationCache['products'];
  isLoading: boolean;
  error: Error | null;
  lastUpdated: number | null;
}

// Singleton registry instance
const registry: RecommendationRegistry = {
  products: [],
  isLoading: false,
  error: null,
  lastUpdated: null,
};

// Listeners for state changes (optional, for future use)
type StateChangeListener = (state: RecommendationRegistry) => void;
const stateChangeListeners: StateChangeListener[] = [];

/**
 * Get current recommendation state
 */
export function getRecommendationState(): Readonly<RecommendationRegistry> {
  return { ...registry };
}

/**
 * Get current recommendations (cached or empty)
 */
export function getRecommendations(): import('./types').RecommendationCache['products'] {
  return [...registry.products];
}

/**
 * Check if recommendations are currently loading
 */
export function isLoading(): boolean {
  return registry.isLoading;
}

/**
 * Check if recommendations have been loaded at least once
 */
export function hasRecommendations(): boolean {
  return registry.products.length > 0 && registry.lastUpdated !== null;
}

/**
 * Update registry state (internal)
 */
function updateRegistry(updates: Partial<RecommendationRegistry>): void {
  Object.assign(registry, updates);
  // Notify listeners
  stateChangeListeners.forEach(listener => listener(getRecommendationState()));
}

/**
 * Initialize recommendations module
 *
 * This should be called once at application bootstrap (e.g., in main.tsx).
 * It starts background loading of recommendations and updates the registry.
 *
 * @param searchService Optional SearchService instance. If not provided,
 *                      a default API service will be created.
 * @param config Optional configuration for the recommendation service
 * @returns Promise that resolves when initial loading is complete
 */
export async function initializeRecommendations(
  searchService?: import('../../services/searchService').SearchService,
  config?: Partial<import('./types').RecommendationConfig>
): Promise<void> {
  // Prevent multiple simultaneous initializations
  if (registry.isLoading) {
    console.log('[Recommendations] Initialization already in progress');
    return;
  }

  updateRegistry({
    isLoading: true,
    error: null,
  });

  try {
    // Import dependencies dynamically to avoid circular dependencies
    const { createSearchService } = await import('../../services');
    const { RecommendationService } = await import('./recommendationService');

    // Use provided service or create default API service
    const service = searchService || createSearchService('api', {
      baseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001',
    });

    // Create and use recommendation service
    const recommendationService = new RecommendationService(service, config);
    const products = await recommendationService.getRecommendations();

    updateRegistry({
      products,
      isLoading: false,
      error: null,
      lastUpdated: Date.now(),
    });

    console.log(`[Recommendations] Initialized with ${products.length} recommendations`);
  } catch (error) {
    const errorObj = error instanceof Error ? error : new Error(String(error));

    updateRegistry({
      isLoading: false,
      error: errorObj,
    });

    console.error('[Recommendations] Failed to initialize:', error);
    // Graceful degradation - registry will have empty products array
  }
}

/**
 * Force refresh of recommendations
 *
 * Clears cache and triggers reload. Useful for manual refresh.
 */
export async function refreshRecommendations(): Promise<void> {
  // Clear cache
  const { clearCache } = await import('./recommendationStorage');
  clearCache();

  // Reinitialize
  await initializeRecommendations();
}

/**
 * Add a state change listener (for future use)
 */
export function addStateChangeListener(listener: StateChangeListener): void {
  stateChangeListeners.push(listener);
}

/**
 * Remove a state change listener
 */
export function removeStateChangeListener(listener: StateChangeListener): void {
  const index = stateChangeListeners.indexOf(listener);
  if (index !== -1) {
    stateChangeListeners.splice(index, 1);
  }
}